import { NgClass, NgIf, UpperCasePipe } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormioForm } from '@formio/angular';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../shared/components/error-messages/error-messages.component';
import { FilterCardComponent } from '../../../shared/components/filter-card/filter-card.component';
import { MatPillComponent } from '../../../shared/components/mat-pill/mat-pill.component';
import { FormUtilitiesService } from '../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { FormEditorComponent } from './form-editor/form-editor.component';
import {
  IForms,
  TAddFormSetting,
  TFormBBulkEditForm,
  TFormBulkEditRequest,
  TFormComponent,
  TFormControlForm,
} from './form.interface';
import { FormStore, IFormsComponentState } from './form.store';

@Component({
  imports: [
    DatatableComponent,
    ErrorMessagesComponent,
    FilterCardComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatCheckbox,
    MatChip,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    NgIf,
    ReactiveFormsModule,
    TranslateModule,
    UpperCasePipe,
    FormEditorComponent,
    MatPillComponent,
  ],
  providers: [FormStore, HttpUtilitiesService],
  selector: 'app-form',
  standalone: true,
  templateUrl: './form.component.html',
})
export class FormComponent extends BaseCrudPageComponent<IForms, IFormsComponentState> implements OnInit {
  @ViewChild('formEditorDialog') formEditorDialog!: TemplateRef<HTMLElement>;
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;

  public datatableConfigurations: IDatatableConfiguration<IForms> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.bulk,
    editScope: ETableCrudScope.bulk,
  };

  public readonly datatableColumns: IDatatableColumn<IForms>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'isActive', isSortable: true },
  ];

  public selectedFormSetting: IForms | null = null;

  constructor(
    public readonly store: FormStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfForm',
      multiContext: 'field.forms',
      nameProperty: 'name',
      singleContext: 'field.form',
    });
  }

  public readonly addEditForm: FormGroup<TFormControlForm> = new FormGroup({
    form: new FormControl<FormioForm | null>(null),
    isActive: new FormControl(true),
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(50)]),
  });

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
        filterCard: this.filterCardData$.pipe(filter(Boolean)),
      }).subscribe(({ datatable, filterCard }) => {
        this.store.loadItems({
          fields: ['name', 'isActive', 'form'],
          filters: [
            ...(filterCard.status !== null && filterCard.status !== undefined
              ? [{ field: 'isActive', ids: [filterCard.status] }]
              : []),
          ],
          limit: datatable.limit,
          page: datatable.page,
          sort: [datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();

    this.addEditForm.reset({
      isActive: true,
      name: '',
    });

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public initializeEdit(): void {
    if (!this.selectedRows().length) {
      return;
    }

    this.addEditForm.enable();
    this.setupForEdit();

    if (this.isBulkEdit()) {
      this.addEditForm.disable();
      this.addEditForm.reset();
    } else {
      const [item] = this.selectedRows();

      this.addEditForm.reset({
        isActive: item.isActive,
        name: item.name,
      });
    }

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    if (!this.isEditDialog()) {
      this.store.createOne(this.formatCreateOnePayload(this.addEditForm.value as IForms));
    } else if (this.isBulkEdit()) {
      this.store.updateMany(this.formatBulkEditPayload(this.addEditForm.value as IForms));
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({ dto: this.formatUpdateOnePayload(this.addEditForm.value as IForms), id: item.id });
    }
  }

  public openFormEditor(formSetting: IForms): void {
    this.selectedFormSetting = formSetting;

    this.matDialog.open(this.formEditorDialog, {
      ...FormEditorComponent.dialogConfig,
      data: formSetting.form,
    });
  }

  public getEditedForm(form: FormioForm): void {
    this.addEditForm.patchValue({ form });
  }

  public onSubmit(): void {
    if (!this.selectedFormSetting?.id) {
      this.matDialog.closeAll();

      return;
    }

    this.store.updateOne({
      dto: {
        form: this.addEditForm.value.form ?? null,
      },
      id: this.selectedFormSetting.id,
    });
  }

  private formatUpdateOnePayload(formSetting: TFormComponent): Partial<IForms> {
    return {
      isActive: formSetting.isActive,
      name: FormUtilitiesService.trimIfString(formSetting.name),
    };
  }

  private formatCreateOnePayload(formSetting: TFormComponent): TAddFormSetting {
    return {
      form: formSetting,
      isActive: formSetting.isActive,
      name: FormUtilitiesService.trimIfString(formSetting.name),
    };
  }

  private formatBulkEditPayload(bulkEditForm: TFormBBulkEditForm): TFormBulkEditRequest[] {
    return this.selectedRows().map((oldData: IForms) => {
      return {
        id: oldData.id,
        ...('isActive' in bulkEditForm ? { isActive: Boolean(bulkEditForm.isActive) } : {}),
      };
    });
  }
}
